import { useMutation, useQuery } from "@apollo/client/react";
import OttoScribblesPawshake from "@assets/imgs/otto-scribbles-pawshake.svg";
import { alert } from "@components/common/Alert";
import { DSButton } from "@components/design-system/molecules";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DELETE_AGENT_SESSION } from "@graphql/mutations/DeleteAgentSession";
import { DELETE_COMPANION_SESSION } from "@graphql/mutations/DeleteCompanionSession";
import { GET_AGENT_SESSIONS } from "@graphql/queries/GetAgentSessions";
import { GET_COMPANION_SESSIONS } from "@graphql/queries/GetCompanionSessions";
import {
  AvailableCompanionTabs,
  AvailableCompanions,
  useCompanion,
} from "@hooks/use-companion";
import {
  isCompanionEffortEnabled,
  useCompanionV2,
} from "@hooks/use-companion-v2";
import Twemoji from "@src/Twemoji";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useEffect, useEffectEvent, useState } from "react";

import { CompanionEffortBadge } from "../../companion-v2/CompanionEffortBadge";
import { LegacyBadge } from "../Buttons";
import DeleteAgentSessionModal from "./DeleteAgentSessionModal";
import DeleteSessionModal from "./DeleteSessionModal";

const SESSIONS_TO_LOAD = 15;

export const CompanionSessionHistory = () => {
  const { companionSession } = useCompanion();
  const { agentSessionUuid: v2SessionUuid } = useCompanionV2();
  const {
    allowCompanionOptIn,
    showGlobalCompanionTierLow,
    showGlobalCompanionTierHigh,
  } = useFlags();
  const tierFlags = {
    showGlobalCompanionTierLow,
    showGlobalCompanionTierHigh,
  };
  const [hasMoreV1, setHasMoreV1] = useState(true);
  const [hasMoreAgent, setHasMoreAgent] = useState(true);
  const { data, refetch, fetchMore, loading } = useQuery(
    GET_COMPANION_SESSIONS,
    {
      variables: { limit: SESSIONS_TO_LOAD, offset: 0 },
      notifyOnNetworkStatusChange: true,
    },
  );
  const {
    data: agentData,
    refetch: refetchAgent,
    fetchMore: fetchMoreAgent,
  } = useQuery(GET_AGENT_SESSIONS, {
    variables: { limit: SESSIONS_TO_LOAD, offset: 0 },
  });
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const sessions = data?.companionSessions ?? [];
  const visibleSessions = allowCompanionOptIn ? sessions : [];
  const agentSessions = (agentData?.agentSessions ?? []).filter(
    Boolean,
  );
  const visibleAgentSessions = agentSessions.filter((s) =>
    isCompanionEffortEnabled(
      s.effort,
      tierFlags,
      allowCompanionOptIn,
    ),
  );
  const sessionCount = sessions.length;
  const agentSessionCount = agentSessions.length;
  const hasMore = hasMoreV1 || hasMoreAgent;

  const loadMoreSessions = async () => {
    if (isFetchingMore || loading || !hasMore) return;

    setIsFetchingMore(true);

    try {
      if (hasMoreAgent && agentSessionCount > 0) {
        const { data: r } = await fetchMoreAgent({
          variables: {
            offset: agentSessionCount,
            limit: SESSIONS_TO_LOAD,
          },
        });
        const returned = r?.agentSessions?.length ?? 0;
        if (returned < SESSIONS_TO_LOAD) setHasMoreAgent(false);
      } else if (hasMoreV1 && sessionCount > 0) {
        const { data: r } = await fetchMore({
          variables: {
            offset: sessionCount,
            limit: SESSIONS_TO_LOAD,
          },
        });
        const returned = r?.companionSessions?.length ?? 0;
        if (returned < SESSIONS_TO_LOAD) setHasMoreV1(false);
      }
    } catch (error) {
      console.error("Failed to load more sessions", error);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const handleScroll = (event) => {
    const target = event.target;
    if (!target) {
      return;
    }

    const threshold = 40;
    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom <= threshold) {
      loadMoreSessions();
    }
  };

  const onSessionChange = useEffectEvent(() => {
    setHasMoreV1(true);
    setHasMoreAgent(true);
    refetch({ limit: SESSIONS_TO_LOAD, offset: 0 });
    refetchAgent({ limit: SESSIONS_TO_LOAD, offset: 0 });
  });

  useEffect(() => {
    onSessionChange();
  }, [companionSession?.companionSessionUuid, v2SessionUuid]);

  useEffect(() => {
    const len = data?.companionSessions?.length;
    if (len === undefined) return;
    setHasMoreV1(len > 0 && len % SESSIONS_TO_LOAD === 0);
  }, [data?.companionSessions?.length]);

  useEffect(() => {
    const len = agentData?.agentSessions?.length;
    if (len === undefined) return;
    setHasMoreAgent(len > 0 && len % SESSIONS_TO_LOAD === 0);
  }, [agentData?.agentSessions?.length]);

  const hasAnySessions =
    visibleSessions.length || visibleAgentSessions.length;
  const showEmptyState = !hasAnySessions && !hasMore;

  return (
    <div className="flex flex-1 flex-col h-full overflow-hidden">
      <div className="overflow-y-auto flex-1" onScroll={handleScroll}>
        {!showEmptyState ? (
          <>
            {visibleAgentSessions.map((i) => (
              <AgentSessionHistoryItem item={i} key={i.uuid} />
            ))}
            {visibleSessions.map((i) => (
              <CompanionSessionHistoryItem
                item={i}
                key={i.companionSessionUuid}
              />
            ))}
            {(isFetchingMore || loading) && (
              <div className="py-4 text-center text-xs text-gray-300">
                Loading more sessions...
              </div>
            )}
            {hasMore && (
              <div className="py-4 text-center">
                <DSButton
                  type="button"
                  onClick={loadMoreSessions}
                  disabled={isFetchingMore || loading}
                  dataTestId="companion-load-more-sessions"
                >
                  Load More Chat Sessions
                </DSButton>
              </div>
            )}
            {!hasMore && (
              <div className="py-4 text-center text-xs text-gray-300">
                You've reached the start of your Companion history.
              </div>
            )}
          </>
        ) : (
          <div
            className="w-full h-full flex justify-center items-center flex-col"
            data-testid="companion-empty-state"
          >
            <img
              src={OttoScribblesPawshake}
              className="w-3/4 max-w-96"
            />
            <div className="text-lg max-w-96 p-4 text-center text-white">
              Welcome to Companion! Click <strong>+ New Chat</strong>{" "}
              to get started, or{" "}
              <FontAwesomeIcon icon={faQuestionCircle} /> to learn
              more.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CompanionSessionHistoryItem = ({ item }) => {
  const [shouldShowDeleteModal, setShouldShowDeleteModal] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    setCompanionSession,
    getCompanionSession,
    setActiveTab,
    companionSession,
  } = useCompanion();

  const [deleteCompanionSession] = useMutation(
    DELETE_COMPANION_SESSION,
    {
      refetchQueries: [
        {
          query: GET_COMPANION_SESSIONS,
          variables: { limit: SESSIONS_TO_LOAD, offset: 0 },
        },
      ],
      awaitRefetchQueries: true,
    },
  );

  if (!item) {
    return null;
  }

  const selectItem = async () => {
    const companionSessionUuid = item.companionSessionUuid;
    const { data: companionSessionData } = await getCompanionSession({
      variables: {
        companionSessionUuid,
      },
    });
    mixpanel.track("Companion Session Selected", {
      companionSessionUuid,
    });
    setCompanionSession(companionSessionData?.companionSession);
    setActiveTab(AvailableCompanionTabs.chat);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShouldShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteCompanionSession({
        variables: {
          companionSessionUuid: item.companionSessionUuid,
        },
      });
      setIsDeleting(false);
      setShouldShowDeleteModal(false);
      alert("success", "Session deleted successfully");

      mixpanel.track("Companion Session Deleted", {
        companionSessionUuid: companionSession?.companionSessionUuid,
      });

      if (
        companionSession?.companionSessionUuid ===
        item.companionSessionUuid
      ) {
        setCompanionSession(null);
      }
    } catch (error) {
      setIsDeleting(false);
      setShouldShowDeleteModal(false);
      alert("error", error.message || "Failed to delete session");
    }
  };

  return (
    <>
      <div
        className="group rounded-xl p-2 my-2 bg-white hover:bg-indigo-100 transition-colors dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 cursor-pointer flex justify-between"
        onClick={selectItem}
        data-testid={`companion-chat-item-${item.companionSessionUuid}`}
      >
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <b className="text-sm truncate">
              {item.title || "New Companion Chat"}
            </b>
            <LegacyBadge />
          </div>
          <span className="text-xs text-gray-500">
            {moment(item.createdAt).format("MMM Do, h:mm A")}
          </span>
          <span className="flex gap-1">
            {item.companionsUsed.map((type) => {
              const emoji = AvailableCompanions[type]?.emoji;
              if (!emoji) {
                return null;
              }
              return (
                <span key={`companion-${type}`}>
                  <Twemoji emoji={emoji} />
                </span>
              );
            })}
          </span>
        </div>

        <div className="group-hover:opacity-100 opacity-0 transition-opacity flex items-center space-x-2">
          <button
            onClick={handleDeleteClick}
            className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-300 rounded-xl"
            title="Delete chat session"
            data-testid={`companion-delete-button-${item.companionSessionUuid}`}
          >
            <FontAwesomeIcon icon={faTrash} className="text-sm" />
          </button>
        </div>
      </div>

      <DeleteSessionModal
        shouldShow={shouldShowDeleteModal}
        hideModal={() => setShouldShowDeleteModal(false)}
        confirmAction={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </>
  );
};

const AgentSessionHistoryItem = ({ item }) => {
  const [shouldShowDeleteModal, setShouldShowDeleteModal] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { setCompanionSession, setActiveTab } = useCompanion();
  const { loadSession, clearSession, agentSessionUuid } =
    useCompanionV2();
  const isActive = agentSessionUuid === item.uuid;

  const [deleteAgentSession] = useMutation(DELETE_AGENT_SESSION, {
    refetchQueries: [
      {
        query: GET_AGENT_SESSIONS,
        variables: { limit: SESSIONS_TO_LOAD, offset: 0 },
      },
    ],
    awaitRefetchQueries: true,
  });

  const selectItem = () => {
    mixpanel.track("Agent Session Selected", {
      agentSessionUuid: item.uuid,
    });
    setCompanionSession(null);
    loadSession(item.uuid);
    setActiveTab(AvailableCompanionTabs.chat);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShouldShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteAgentSession({
        variables: { agentSessionUuid: item.uuid },
      });
      setIsDeleting(false);
      setShouldShowDeleteModal(false);
      alert("success", "Session deleted successfully");

      mixpanel.track("Agent Session Deleted", {
        agentSessionUuid: item.uuid,
      });

      if (isActive) {
        clearSession();
      }
    } catch (error) {
      setIsDeleting(false);
      setShouldShowDeleteModal(false);
      alert("error", error.message || "Failed to delete session");
    }
  };

  return (
    <>
      <div
        className={`group rounded-xl p-2 my-2 transition-colors cursor-pointer dark:text-gray-300 flex justify-between ${
          isActive
            ? "bg-indigo-200 dark:bg-gray-700"
            : "bg-white hover:bg-indigo-100 dark:bg-gray-800 dark:hover:bg-gray-700"
        }`}
        onClick={selectItem}
        data-testid={`agent-chat-item-${item.uuid}`}
      >
        <div className="flex flex-col min-w-0 gap-1">
          <b className="text-sm truncate">
            {item.title || "New Chat"}
          </b>
          <span className="text-xs text-gray-500">
            {moment(item.createdAt).format("MMM Do, h:mm A")}
          </span>
          {item.effort && (
            <div>
              <CompanionEffortBadge effort={item.effort} />
            </div>
          )}
        </div>

        <div className="group-hover:opacity-100 opacity-0 transition-opacity flex items-center space-x-2">
          <button
            onClick={handleDeleteClick}
            className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-300 rounded-xl"
            title="Delete chat session"
            data-testid={`agent-delete-button-${item.uuid}`}
          >
            <FontAwesomeIcon icon={faTrash} className="text-sm" />
          </button>
        </div>
      </div>

      <DeleteAgentSessionModal
        isOpen={shouldShowDeleteModal}
        onClose={() => setShouldShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </>
  );
};
